<template>
    <div>
        <table class="w-full">
            <tr v-if="get.discount > 0" class="text-blue">
                <td class="text-xs">
                    <Popper :content="$t('compared_to_monthly_payment_you_save')">
                        <div class="text-xs flex flex-row gap-1 items-center cursor-pointer">
                            {{ $t('you_save') }}
                            <Icon type="InfoCircle" viewBox="0 0 20 20" width="15" height="15" class="fill-current" />
                        </div>
                    </Popper>
                </td>
                <td class="text-right font-bold text-xs">- {{ $toCurrency(get.discount) }}</td>
            </tr>

            <tr v-if="get.discount2 > 0" class="text-blue">
                <td class="text-xs">
                    <Popper :content="$t('combined_discount_amount_message')">
                        <div class="text-xs flex flex-row gap-1 items-center cursor-pointer">
                            {{ $t('previous_bookings_discount') }}
                            <Icon type="InfoCircle" viewBox="0 0 20 20" width="15" height="15" class="fill-current" />
                        </div>
                    </Popper>
                </td>
                <td class="text-right font-bold text-xs">- {{ $toCurrency(get.discount2) }}</td>
            </tr>
            <tr v-if="get.discount_coupon > 0 && get.coupon_code" class="text-blue">
                <td class="text-xs">
                    <Popper :content="$t('discount_voucher')">
                        <div class="text-xs flex flex-row gap-1 items-center cursor-pointer">
                            {{ $t('coupon_code') }}: {{ get.coupon_code }}
                            <Icon type="InfoCircle" viewBox="0 0 20 20" width="15" height="15" class="fill-current" />
                        </div>
                    </Popper>

                    <br />

                    <button :disabled="removeCouponLoading" class="underline" @click="removeCoupon">{{ $t('plain_remove') }}</button>
                </td>
                <td class="text-right font-bold text-xs">- {{ $toCurrency(get.discount_coupon) }}</td>
            </tr>
            <tr v-if="get.discount_abonnement > 0" class="text-blue">
                <td class="text-xs">
                    <Popper :content="$t('changing_term_credit_for_booked_subscription')">
                        <div class="text-xs flex flex-row gap-1 items-center cursor-pointer">
                            {{ $t('discount_abonnement') }}
                            <Icon type="InfoCircle" viewBox="0 0 20 20" width="15" height="15" class="fill-current" />
                        </div>
                    </Popper>
                </td>
                <td class="text-right font-bold text-xs">- {{ $toCurrency(get.discount_abonnement) }}</td>
            </tr>
            <tr v-if="$store.getters['user/isBusinessUser']()">
                <td class="text-xs">{{ $t('subtotal_excl_VAT') }}</td>
                <td class="text-right font-bold text-xs">{{ $toCurrency(get.total_price_net) }}</td>
            </tr>
            <tr v-if="$store.getters['user/isPrivateUser']()">
                <td class="text-xs">{{ $t('subtotal_inclVAT') }}</td>
                <td class="text-right font-bold text-xs">{{ $toCurrency(get.total_price_gross) }}</td>
            </tr>

            <tr v-for="(taxValue, taxRate) in get.taxes" :key="taxRate">
                <td class="text-xs">
                    <Popper
                        :content="`${$t('vat_description_written_anaylse')} ${$toPercent(Number(Object.keys(get.taxes)[0]))}.
                         ${Object.keys(get.taxes)[1] ? `${$t('vat_description_video_anaylse')}  ${$toPercent(Number(Object.keys(get.taxes)[1]))}` : ''}`"
                    >
                        <div class="text-xs flex flex-row gap-1 items-center cursor-pointer my-1">
                            {{
                                $t('payment_vat', {
                                    tax: $toPercent(Number(taxRate))
                                })
                            }}
                            <Icon type="InfoCircle" viewBox="0 0 20 20" width="15" height="15" class="fill-current" />
                        </div>
                    </Popper>
                </td>
                <td class="text-right font-bold text-xs">
                    {{ $toCurrency(taxValue) }}
                </td>
            </tr>
        </table>

        <hr class="border-t border-gray-300 my-4" />

        <table class="w-full">
            <tbody>
                <tr>
                    <td class="text-xs">{{ $t('payment_total_end_price') }}</td>
                    <td id="cart_total_end_price" class="text-right font-bold text-xs">{{ $toCurrency(get.total_end_price_gross) }}</td>
                </tr>
            </tbody>
        </table>

        <div v-if="!$isOriginalCurrency()">
            <table class="w-full">
                <tbody>
                    <tr class="text-blue">
                        <td class="text-xs">
                            <b>{{ $t('payment_hint_local_currency') }}</b>
                        </td>
                        <td id="cart_total_end_price" class="text-right font-bold text-xs">{{ $toCurrency(get.total_end_price_gross, 2, true) }}</td>
                    </tr>
                </tbody>
            </table>
        </div>
        <PriceWheelEligibility v-if="isWheelActive" :difference-price="differencePrice" :total-end-price-gross="get.total_end_price_gross" :min-order-value="wheel?.minOrderValue">
        </PriceWheelEligibility>
        <PriceRaffleEligibility v-if="isRaffleActive" :difference-price="differenceRaffle" :total-end-price-gross="get.total_end_price_gross" :min-order-value="raffle?.minOrderValue">
        </PriceRaffleEligibility>
        <slot></slot>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import api from '../../api.js';
import PriceWheelEligibility from '../PriceWheelEligibility.vue';
import PriceRaffleEligibility from '../PriceRaffleEligibility.vue';
import { getRegion } from '../../helpers.js';

export default {
    components: { PriceWheelEligibility, PriceRaffleEligibility },
    props: {
        data: Object
    },
    emits: ['reload'],
    data: () => ({
        removeCouponLoading: false
    }),

    computed: {
        ...mapGetters('cart', ['get', 'getItems']),
        wheel() {
            return this.get.wheel || null;
        },
        today() {
            return new Date();
        },
        region() {
            return getRegion();
        },
        isWheelActive() {
            if (this.region !== 'de' || this.wheel?.active !== true || !this.wheel?.start || !this.wheel?.end) return false;

            const today = new Date();
            const start = new Date(this.wheel.start);
            const end = new Date(this.wheel.end);

            return start <= today && today <= end;
        },
        differencePrice() {
            return -this.wheel?.div;
        },
        raffle() {
            return this.get.raffle || null;
        },
        isRaffleActive() {
            // An den Dashboard-Banner "WM Aktion 26" gekoppelt (Backend: Cart::getRaffleAttribute).
            // Region wird bereits backend-seitig über den Banner bestimmt.
            return this.raffle?.active === true;
        },
        differenceRaffle() {
            return -this.raffle?.div;
        }
    },
    methods: {
        getRegion,
        removeCoupon() {
            this.removeCouponLoading = true;
            api.delete(`/cart/coupon`)
                .then(async () => {
                    this.$toast.success(this.$t('coupon_succesfully_removed'));
                    await this.$store.dispatch('cart/index');
                    this.removeCouponLoading = false;
                    this.$emit('reload');
                })
                .catch(() => {
                    this.$toast.error(this.$t('coupon_error_removing'));
                    this.removeCouponLoading = false;
                });
        }
    }
};
</script>
